/**
 * Tracé des liens de dépendance (FS/SS/FF/SF) en polylignes à angles droits, et calcul
 * de la contrainte de date qu'une dépendance impose à la tâche successeure.
 *
 * Fonctions pures : les rectangles de barre `{x, y, width, height}` sont fournis par l'appelant
 * (issus de scale.js + layout.js), aucun accès DOM ici.
 */

import { addDays } from './scale.js';

/** Décalage horizontal (px) avant que la polyligne ne tourne, de part et d'autre des barres. */
const ELBOW = 12;

/** Ancre de départ/arrivée par type de dépendance, en termes de bord de barre. */
const ANCHORS = Object.freeze({
  FS: { from: 'end', to: 'start' },
  SS: { from: 'start', to: 'start' },
  FF: { from: 'end', to: 'end' },
  SF: { from: 'start', to: 'end' },
});

export const DEPENDENCY_TYPES = Object.freeze(['FS', 'SS', 'FF', 'SF']);

function anchorPoint(rect, side) {
  const x = side === 'start' ? rect.x : rect.x + rect.width;
  const y = rect.y + rect.height / 2;
  return { x, y };
}

function dedupe(points) {
  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const prev = out[out.length - 1];
    const cur = points[i];
    if (prev.x !== cur.x || prev.y !== cur.y) out.push(cur);
  }
  return out;
}

/**
 * Calcule les points d'une polyligne à angles droits reliant la barre `from` (prédécesseure)
 * à la barre `to` (successeure), selon le `type` de dépendance. Chaque rect est
 * `{x, y, width, height}` en pixels, issu du layout déjà résolu.
 */
export function buildDependencyPath({ from, to, type }) {
  const spec = ANCHORS[type];
  if (!spec) {
    throw new Error(`Type de dépendance inconnu : ${type}`);
  }

  const start = anchorPoint(from, spec.from);
  const end = anchorPoint(to, spec.to);

  const startDir = spec.from === 'end' ? 1 : -1;
  const endDir = spec.to === 'end' ? 1 : -1;

  const p1 = { x: start.x + startDir * ELBOW, y: start.y };
  const p2 = { x: end.x + endDir * ELBOW, y: end.y };
  const midX = (p1.x + p2.x) / 2;

  return dedupe([start, p1, { x: midX, y: p1.y }, { x: midX, y: p2.y }, p2, end]);
}

/** Sérialise une liste de points `{x,y}` en attribut `points` pour un `<polyline>` SVG. */
export function pathToPolylinePoints(points) {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

/** Bord du prédécesseur porteur de la contrainte, par type de dépendance. */
function predecessorEdge(type) {
  return type === 'SS' || type === 'SF' ? 'startDate' : 'dueDate';
}

/** Bord du successeur contraint, par type de dépendance. */
export function successorEdge(type) {
  return type === 'FF' || type === 'SF' ? 'dueDate' : 'startDate';
}

/**
 * Date minimale imposée au successeur par une dépendance, `lagDays` inclus (négatif autorisé,
 * pour représenter un chevauchement volontaire).
 *
 *   FS: succ.startDate >= pred.dueDate   + lag
 *   SS: succ.startDate >= pred.startDate + lag
 *   FF: succ.dueDate   >= pred.dueDate   + lag
 *   SF: succ.dueDate   >= pred.startDate + lag
 */
export function constraintDate({ predecessor, type, lagDays = 0 }) {
  if (!DEPENDENCY_TYPES.includes(type)) {
    throw new Error(`Type de dépendance inconnu : ${type}`);
  }
  const anchorDate = predecessor[predecessorEdge(type)];
  return addDays(anchorDate, lagDays);
}
